import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "../../lib/apiClient";

/**
 * SiteAutocomplete — free-text Site Name with a suggestion dropdown built
 * from prior sites used for the current customer. Picking a suggestion
 * fills site_name + site_address + postcode in one click.
 *
 * Props:
 *   customerName  — the currently selected customer (used to scope sites)
 *   value         — current site_name string
 *   onChange      — (siteName) → void
 *   onPick        — ({site_name, site_address, postcode}) → void (optional)
 *                   Fires when user picks a saved site so the parent can
 *                   also fill the address / postcode boxes.
 */
export function SiteAutocomplete({ customerName, value, onChange, onPick, testid = "site-name-input", placeholder = "e.g. Warehouse 1, Unit 4B" }) {
  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const wrapRef = useRef(null);

  // Outside-click close
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Whenever the customer changes, reload the site list
  useEffect(() => {
    const cn = (customerName || "").trim();
    if (!cn) {
      setSites([]);
      return;
    }
    api
      .get(`/customer-sites?customer_name=${encodeURIComponent(cn)}`)
      .then((r) => setSites(r.data || []))
      .catch(() => setSites([]));
  }, [customerName]);

  const q = (value || "").trim().toLowerCase();
  const filtered = q
    ? sites.filter((s) =>
        (s.site_name || "").toLowerCase().includes(q) ||
        (s.site_address || "").toLowerCase().includes(q))
    : sites;

  const pick = (s) => {
    onChange(s.site_name || "");
    if (onPick) onPick(s);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if (sites.length) setOpen(true); }}
        className="qb-input"
        data-testid={testid}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-300 shadow-lg"
          data-testid={`${testid}-suggestions`}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
            Previous sites for {customerName}
          </div>
          {filtered.map((s, i) => (
            <button
              key={`${s.site_name}|${s.site_address}|${i}`}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 outline-none flex items-start gap-2"
              data-testid={`${testid}-suggestion-${i}`}
            >
              <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block font-medium text-slate-900 truncate">{s.site_name || "(no site name)"}</span>
                {(s.site_address || s.postcode) && (
                  <span className="block text-xs text-slate-500 truncate">
                    {[s.site_address, s.postcode].filter(Boolean).join(" · ")}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
